import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Switch } from "@/shared/components/ui/switch";
import { Label } from "@/shared/components/ui/label";
import api from "@/shared/utils/api";
import {
  ACCENT_BUTTON_CLASS,
  ACCENT_SWITCH_CLASS,
  currentPagePath,
  ENDPOINT,
} from "./consentConstants";
import type { ConsentSaveResponse, ConsentState } from "./consentTypes";

export const OPEN_EVENT = "gc:open-privacy-choices";

const GENERIC_ERROR =
  "We couldn't save your choices. Please try again.";

export interface PrivacyChoicesModalProps {
  initialState?: ConsentState | null;
}

const PrivacyChoicesModal: React.FC<PrivacyChoicesModalProps> = ({
  initialState = null,
}) => {
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(
    !!initialState?.preferences?.analytics
  );
  const [advertising, setAdvertising] = useState(
    !!initialState?.preferences?.advertising
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Last-saved preferences, so reopening reflects saved changes (or the initial
  // hydrated state) rather than abandoned edits from a cancelled session.
  const committed = useRef({
    analytics: !!initialState?.preferences?.analytics,
    advertising: !!initialState?.preferences?.advertising,
  });

  useEffect(() => {
    const handleOpen = () => {
      setAnalytics(committed.current.analytics);
      setAdvertising(committed.current.advertising);
      setError(null);
      setOpen(true);
    };
    document.addEventListener(OPEN_EVENT, handleOpen);
    return () => document.removeEventListener(OPEN_EVENT, handleOpen);
  }, []);

  const close = () => setOpen(false);

  const persist = async (prefs: { analytics: boolean; advertising: boolean }) => {
    setSaving(true);
    setError(null);
    try {
      const payload = (await api.post(ENDPOINT, {
        preferences: prefs,
        pagePath: currentPagePath(),
        source: "modal",
        ...(initialState?.schoolId != null
          ? { schoolId: initialState.schoolId }
          : {}),
      })) as ConsentSaveResponse;

      if (!payload?.success) {
        setError(GENERIC_ERROR);
        return;
      }

      committed.current = prefs;
      setAnalytics(prefs.analytics);
      setAdvertising(prefs.advertising);
      window.gcConsentRuntime?.apply({
        ...(initialState ?? {}),
        preferences: prefs,
        jurisdiction: payload.data?.jurisdiction ?? initialState?.jurisdiction,
        gpcDetected: payload.data?.gpcDetected ?? initialState?.gpcDetected ?? false,
        setAt: payload.data?.setAt ?? new Date().toISOString(),
      });
      setOpen(false);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const saveChoices = () => persist({ analytics, advertising });
  const acceptAll = () => persist({ analytics: true, advertising: true });
  const rejectAll = () => persist({ analytics: false, advertising: false });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && close()}>
      <DialogContent className="rounded-lg sm:rounded-lg">
        <DialogHeader className="text-left">
          <DialogTitle>Your privacy choices</DialogTitle>
          <DialogDescription>
            Choose what we can track about your visit. You can change these
            settings anytime.
          </DialogDescription>
        </DialogHeader>

        <div
          role="group"
          aria-label="Cookie categories"
          className="divide-y divide-gray-200 border-t border-gray-200"
        >
          <div className="flex items-center justify-between gap-4 py-3">
            <div className="cursor-not-allowed">
              <Label htmlFor="consent-necessary" className="cursor-not-allowed">
                Necessary
              </Label>
              <p className="text-sm text-gray-600">
                Required for the site to work.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Switch
                id="consent-necessary"
                checked
                disabled
                aria-label="Necessary cookies (always on)"
                className={`${ACCENT_SWITCH_CLASS} opacity-50 cursor-not-allowed`}
              />
              <span className="text-xs text-gray-500">Always on</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 py-3">
            <div>
              <Label htmlFor="consent-analytics">Analytics</Label>
              <p className="text-sm text-gray-600">
                Helps us understand how the site is used.
              </p>
            </div>
            <Switch
              id="consent-analytics"
              checked={analytics}
              onCheckedChange={setAnalytics}
              disabled={saving}
              className={`${ACCENT_SWITCH_CLASS} shrink-0`}
            />
          </div>

          <div className="flex items-center justify-between gap-4 py-3">
            <div>
              <Label htmlFor="consent-advertising">Advertising / Marketing</Label>
              <p className="text-sm text-gray-600">
                Used to show you more relevant content.
              </p>
            </div>
            <Switch
              id="consent-advertising"
              checked={advertising}
              onCheckedChange={setAdvertising}
              disabled={saving}
              className={`${ACCENT_SWITCH_CLASS} shrink-0`}
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-primary-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
          <Button
            variant="primary"
            onClick={acceptAll}
            disabled={saving}
            className={`${ACCENT_BUTTON_CLASS} w-full rounded-lg sm:order-3 sm:w-auto`}
          >
            Accept all
          </Button>
          <Button
            variant="neutral"
            onClick={rejectAll}
            disabled={saving}
            className="w-full rounded-lg sm:order-1 sm:w-auto"
          >
            Reject all
          </Button>
          <Button
            variant="neutral"
            onClick={saveChoices}
            disabled={saving}
            className="w-full rounded-lg sm:order-2 sm:ml-auto sm:w-auto"
          >
            Save my choices
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyChoicesModal;
